import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Bus, Pencil } from "lucide-react";

import { transportService, type DriverOption, type Trip, type Vehicle, type VehicleMaintenance } from "../../services/transportService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { errorLine } from "./TransportParts";
import { FUEL_TYPES, VEHICLE_TYPES, day, labelFrom, maintenanceStatus, money, papers, vehicleConfig, vehicleStatus } from "./transportLogic";

const FEATURES: [keyof Vehicle, string][] = [
  ["has_seatbelts", "Seatbelts"],
  ["has_gps", "GPS tracker"],
  ["has_camera", "Camera"],
  ["has_air_conditioning", "Air conditioning"],
  ["has_wifi", "Wi-Fi"],
];

function VehicleForm({ vehicle, drivers, onClose, onDone }: { vehicle?: Vehicle; drivers: DriverOption[]; onClose: () => void; onDone: (vehicle: Vehicle) => void }) {
  const [seats, setSeats] = useState(String(vehicle?.seating_capacity ?? ""));
  const [standing, setStanding] = useState(String(vehicle?.standing_capacity ?? 0));
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (vehicle ? transportService.updateVehicle({ id: vehicle.id, data }) : transportService.createVehicle(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    const data: Record<string, unknown> = {
      name: text("name"),
      registration_number: text("registration_number").toUpperCase(),
      vehicle_type: text("vehicle_type"),
      fuel_type: text("fuel_type"),
      make: text("make"),
      model: text("model"),
      year: Number(text("year")),
      color: text("color"),
      seating_capacity: Number(seats),
      standing_capacity: Number(standing || 0),
      total_capacity: Number(seats) + Number(standing || 0),
      assigned_driver: text("assigned_driver") ? Number(text("assigned_driver")) : null,
      insurance_number: text("insurance_number") || null,
      insurance_expiry: text("insurance_expiry") || null,
      permit_number: text("permit_number") || null,
      permit_expiry: text("permit_expiry") || null,
      engine_number: text("engine_number") || null,
      chassis_number: text("chassis_number") || null,
      engine_capacity: text("engine_capacity") || null,
      purchase_date: text("purchase_date") || null,
      purchase_price: text("purchase_price") || null,
      current_value: text("current_value") || null,
      parking_location: text("parking_location") || null,
      notes: text("notes") || null,
    };
    for (const [key] of FEATURES) data[key] = form.get(key) === "on";
    if (!vehicle) data.is_active = true;
    save.mutate(data);
  };
  const value = (name: keyof Vehicle) => (vehicle?.[name] as string | number | null | undefined) ?? "";

  return (
    <FormModal
      id="vehicle"
      title={vehicle ? `Edit ${vehicle.name}` : "Add a vehicle"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The vehicle could not be saved.") : undefined}
      saveLabel={vehicle ? "Save changes" : "Add the vehicle"}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The vehicle" divided={false}>
        <Field label="Name" hint="What staff call it, e.g. Bus 2 or The Blue Coaster.">
          <input required name="name" maxLength={100} defaultValue={value("name")} className="input mt-1" />
        </Field>
        <Field label="Number plate">
          <input required name="registration_number" maxLength={20} defaultValue={value("registration_number")} className="input mt-1 uppercase" placeholder="e.g. UBA 123X" />
        </Field>
        <Field label="Type">
          <select name="vehicle_type" defaultValue={value("vehicle_type") || "bus"} className="input mt-1">
            {VEHICLE_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Fuel">
          <select name="fuel_type" defaultValue={value("fuel_type") || "diesel"} className="input mt-1">
            {FUEL_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Make">
          <input required name="make" maxLength={50} defaultValue={value("make")} className="input mt-1" placeholder="e.g. Toyota" />
        </Field>
        <Field label="Model">
          <input required name="model" maxLength={50} defaultValue={value("model")} className="input mt-1" placeholder="e.g. Coaster" />
        </Field>
        <Field label="Year">
          <input required type="number" name="year" min={1950} max={new Date().getFullYear() + 1} defaultValue={value("year")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Colour">
          <input required name="color" maxLength={30} defaultValue={value("color")} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Places and driver" columns={3}>
        <Field label="Seats">
          <input required type="number" min={1} value={seats} onChange={(event) => setSeats(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Standing" hint={seats ? `${Number(seats) + Number(standing || 0)} places in all.` : undefined}>
          <input type="number" min={0} value={standing} onChange={(event) => setStanding(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Driver" hint="Only staff with the bus driver role.">
          <select name="assigned_driver" defaultValue={value("assigned_driver")} className="input mt-1">
            <option value="">No driver yet</option>
            {drivers.map((driver) => <option key={driver.id} value={driver.id}>{driver.full_name}</option>)}
          </select>
        </Field>
      </FormSection>

      <FormSection title="Papers" description="Warned about a month before they run out.">
        <Field label="Insurance policy" optional>
          <input name="insurance_number" maxLength={50} defaultValue={value("insurance_number")} className="input mt-1" />
        </Field>
        <Field label="Insurance ends" optional>
          <input type="date" name="insurance_expiry" defaultValue={value("insurance_expiry")} className="input mt-1" />
        </Field>
        <Field label="Operating permit" optional>
          <input name="permit_number" maxLength={50} defaultValue={value("permit_number")} className="input mt-1" />
        </Field>
        <Field label="Permit ends" optional>
          <input type="date" name="permit_expiry" defaultValue={value("permit_expiry")} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Features">
        <div className="grid gap-2 text-sm text-gray-700 sm:col-span-2 sm:grid-cols-3">
          {FEATURES.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <input type="checkbox" name={key} defaultChecked={vehicle ? Boolean(vehicle[key]) : key === "has_seatbelts"} className="h-4 w-4 rounded border-gray-300" /> {label}
            </label>
          ))}
        </div>
        <Field label="Parked at night" optional className="sm:col-span-2">
          <input name="parking_location" maxLength={200} defaultValue={value("parking_location")} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Engine and purchase" description="Optional; useful for insurance and the asset register." columns={3}>
        <Field label="Engine number" optional>
          <input name="engine_number" maxLength={50} defaultValue={value("engine_number")} className="input mt-1" />
        </Field>
        <Field label="Chassis number" optional>
          <input name="chassis_number" maxLength={50} defaultValue={value("chassis_number")} className="input mt-1" />
        </Field>
        <Field label="Engine size" optional hint="e.g. 4200 cc.">
          <input name="engine_capacity" maxLength={20} defaultValue={value("engine_capacity")} className="input mt-1" />
        </Field>
        <Field label="Bought on" optional>
          <input type="date" name="purchase_date" defaultValue={value("purchase_date")} className="input mt-1" />
        </Field>
        <Field label="Price (UGX)" optional>
          <input type="number" name="purchase_price" min={0} step="any" defaultValue={value("purchase_price")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Worth now (UGX)" optional>
          <input type="number" name="current_value" min={0} step="any" defaultValue={value("current_value")} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Notes" optional className="sm:col-span-2 lg:col-span-3">
          <textarea name="notes" rows={2} defaultValue={value("notes")} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** The fleet: who drives what, whether the papers are in date, and each vehicle's workshop history. */
export function VehiclesPanel({ vehicles, drivers, trips, maintenance, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "papers" from the dashboard. */
  initialView?: string;
  vehicles?: Vehicle[];
  drivers: DriverOption[];
  trips: Trip[];
  maintenance: VehicleMaintenance[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Vehicle | "new" | null>(null);
  const retire = useMutation({
    mutationFn: transportService.updateVehicle,
    onSuccess: (vehicle) => {
      setNotice({ tone: "success", text: vehicle.is_active ? `${vehicle.name} is back in the fleet.` : `${vehicle.name} is off the fleet; it can no longer be given trips.` });
      onChanged();
    },
  });

  const columns: RegisterColumn<Vehicle>[] = [
    {
      header: "Vehicle",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.name}</span>
          <span className="block text-xs text-gray-500">{row.registration_number}</span>
        </span>
      ),
      exportValue: (row) => row.name,
      className: "min-w-[9rem]",
    },
    { header: "Number plate", cell: (row) => row.registration_number, exportValue: (row) => row.registration_number, exportOnly: true },
    { header: "Make and model", cell: (row) => `${row.make} ${row.model} (${row.year})`, exportValue: (row) => `${row.make} ${row.model} ${row.year}`, className: "text-gray-700" },
    { header: "Seats", cell: (row) => row.seating_capacity, exportValue: (row) => row.seating_capacity, align: "right" },
    { header: "Driver", cell: (row) => row.assigned_driver_name || <span className="text-amber-700">None</span>, exportValue: (row) => row.assigned_driver_name ?? "", className: "text-gray-700" },
    {
      header: "Papers",
      cell: (row) => {
        const list = papers(row, today);
        if (!list.length) return <span className="text-gray-300">—</span>;
        const worst = list.find((paper) => paper.expired) ?? list.find((paper) => paper.soon);
        return worst ? (
          <span className={worst.expired ? "font-medium text-red-700" : "text-amber-800"}>{worst.label} {worst.expired ? "expired" : `ends ${worst.expiry}`}</span>
        ) : (
          <span className="text-gray-700">In date</span>
        );
      },
      exportValue: (row) => papers(row, today).map((paper) => `${paper.label} ${paper.expiry}`).join("; "),
    },
    { header: "Status", cell: (row) => <StatusDot {...vehicleStatus(row, today)} />, exportValue: (row) => vehicleStatus(row, today).label },
  ];

  const vehicle = viewing !== null ? vehicles?.find((row) => row.id === viewing) : undefined;
  const jobs = vehicle ? maintenance.filter((row) => row.vehicle === vehicle.id) : [];
  const nextTrips = vehicle ? trips.filter((row) => row.vehicle === vehicle.id && row.trip_date >= today && row.status === "scheduled").length : 0;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Vehicles"
        description={canManage ? "Open a vehicle for its papers and workshop history, or to edit it." : "The school's vehicles."}
        actions={canManage && (
          <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Bus className="mr-1.5 h-3.5 w-3.5" /> Add a vehicle
          </button>
        )}
        rows={vehicles}
        config={vehicleConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          retire.reset();
          setViewing(row.id);
        }}
        noun={["vehicle", "vehicles"]}
        exportName={{ file: "transport-fleet", title: "School Fleet" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No vehicles yet. Add the first with Add a vehicle." : "No vehicles to show."}
      />

      {vehicle && !editing && (
        <DetailsModal
          size="3xl"
          title={`${vehicle.name} · ${vehicle.registration_number}`}
          headline={{ label: "Driver", value: vehicle.assigned_driver_name || "None yet", note: `${vehicle.make} ${vehicle.model} ${vehicle.year} · ${vehicle.color} · ${vehicle.seating_capacity} seats${vehicle.standing_capacity ? ` + ${vehicle.standing_capacity} standing` : ""}` }}
          status={vehicleStatus(vehicle, today)}
          notice={
            papers(vehicle, today).some((paper) => paper.expired || paper.soon) ? (
              <div className="space-y-1">
                {papers(vehicle, today)
                  .filter((paper) => paper.expired || paper.soon)
                  .map((paper) => (
                    <p key={paper.label} className={`rounded-md px-3 py-2 text-sm ${paper.expired ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-900"}`}>
                      {paper.label} {paper.expired ? `ran out on ${paper.expiry}` : `runs out on ${paper.expiry}, in ${paper.days} ${paper.days === 1 ? "day" : "days"}`}.
                    </p>
                  ))}
              </div>
            ) : undefined
          }
          facts={[
            ["Type", labelFrom(VEHICLE_TYPES, vehicle.vehicle_type)],
            ["Fuel", labelFrom(FUEL_TYPES, vehicle.fuel_type)],
            ["Insurance", [vehicle.insurance_number, vehicle.insurance_expiry && `to ${day(vehicle.insurance_expiry)}`].filter(Boolean).join(" ") || undefined],
            ["Permit", [vehicle.permit_number, vehicle.permit_expiry && `to ${day(vehicle.permit_expiry)}`].filter(Boolean).join(" ") || undefined],
            ["Features", FEATURES.filter(([key]) => vehicle[key]).map(([, label]) => label).join(", ") || "None recorded"],
            ["Parked at", vehicle.parking_location ?? undefined],
            ["Trips booked", nextTrips ? `${nextTrips} from today` : "None"],
            ["Engine / chassis", [vehicle.engine_number, vehicle.chassis_number].filter(Boolean).join(" / ") || undefined],
            ["Bought", [day(vehicle.purchase_date), money(vehicle.purchase_price)].filter(Boolean).join(" for ") || undefined],
            ["Worth now", money(vehicle.current_value) || undefined],
          ]}
          sections={[{ title: "Notes", body: vehicle.notes }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && vehicle.status !== "maintenance" && (
                <button
                  type="button"
                  disabled={retire.isPending || (vehicle.is_active && nextTrips > 0)}
                  title={vehicle.is_active && nextTrips > 0 ? "Call off its booked trips first." : undefined}
                  onClick={() => retire.mutate({ id: vehicle.id, data: { is_active: !vehicle.is_active } })}
                  className="btn btn-outline"
                >
                  {vehicle.is_active ? "Take off the fleet" : "Put back in the fleet"}
                </button>
              )}
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(vehicle)}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          {retire.isError && errorLine(retire.error, "The vehicle could not be changed.")}
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Workshop history ({jobs.length})</h4>
            {jobs.length ? (
              <ul className="mt-1.5 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {jobs.slice(0, 8).map((job) => (
                  <li key={job.id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="tabular-nums text-gray-500">{day(job.requested_date)}</span>
                      <span className="ml-2 font-medium text-gray-900">{job.title}</span>
                      {job.work_performed && <span className="block text-gray-700">{job.work_performed}</span>}
                    </span>
                    <span className="flex items-center gap-3">
                      {job.actual_cost && <span className="tabular-nums text-gray-600">{money(job.actual_cost)}</span>}
                      <StatusDot {...maintenanceStatus(job)} />
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">No workshop visits recorded.</p>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <VehicleForm
          vehicle={editing === "new" ? undefined : editing}
          drivers={drivers}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `${saved.name} (${saved.registration_number}) added to the fleet.` : `${saved.name} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
